'use client';

import { usePathname } from 'next/navigation';

import { ArrowLeft } from 'lucide-react';

import { Trans } from '@kit/ui/trans';

/**
 * Back arrow at the top of the auth card. Resets the CURRENT auth page (plain
 * <a> to the pathname without query params — a full navigation remounts the
 * client step machines), so someone deep in a flow (code entry, password
 * step, verify steps) can bail out and pick another method.
 *
 * Visibility is pure CSS: the methods containers mark their FIRST screen with
 * a `data-auth-first-screen` element (the same slot that carries the
 * alternative-methods switcher), and the card (`group`) hides this link while
 * that marker is present — no step-state plumbing needed. Screens past the
 * first render no marker, so the link appears.
 */
export function AuthBackLink() {
  const pathname = usePathname();

  // Sign-in/up reset to their own first screen; auxiliary auth pages
  // (password-reset, verify, …) send the user back to sign-in.
  const href = pathname?.startsWith('/auth/sign-') ? pathname : '/auth/sign-in';

  return (
    <a
      href={href}
      className="text-muted-foreground hover:text-foreground -mb-2 flex items-center gap-1.5 self-start text-sm transition-colors group-has-[[data-auth-first-screen]]:hidden"
      data-test="auth-back-link"
    >
      <ArrowLeft className="h-4 w-4 rtl:-scale-x-100" />
      <Trans i18nKey="common:back" defaults="Back" />
    </a>
  );
}
